import { DISMISS, useNotification } from "collections";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { DOCS, DESKTOP_PROXY_AGENT } from "Constants";
import {
  CancelButton,
  DetailsLoader,
  FilterOption,
  HelpLink,
  NoResultsIndicator,
  NoResultsIndicatorAnimation,
  SubmitButton
} from "elements";
import {
  PolicyFilter,
  usePolicyFilterValue
} from "elements/Filter/PolicyFilter";
import {
  KeyBucket,
  Maybe,
  PolicyDeploymentScope,
  PolicyType,
  useAllPoliciesGroupsQuery,
  useDeleteGroupPoliciesMutation,
  useDeletePoliciesMutation,
  useDeviceCountsQuery,
  useGroupPoliciesQuery,
  useHardwareModelsQuery,
  usePoliciesQuery,
  useProvisioningAccountsQuery,
  useSitesForTenantQuery,
  HardwareModel,
  useUserGroupsForTenantQuery,
  useGetUserGroupDevicesQuery
} from "generated/graphql";
import { uniq } from "lodash";
import { noOpNotifyContext } from "polygraphql";
import { Redirect, Route, RouteComponentProps, Switch } from "polyrouter";
import Link, { infiniteNestLink } from "polyrouter/Link";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Trans, useTranslation } from "utilities/i18n";
import { useSelector } from "react-redux";
import { GenericBucket, isArrayOfGenericBucket } from "routes/Home/bucketTypes";
import { State } from "store";
import { SortState } from "store/filters";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { handleNullFields, nameIsUnassigned } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { useDeviceGroups } from "../DeviceGroups/DeviceGroupsUtils";
import { PolicyTypes } from "../Inventory/Settings-Desktop/ProvisionedDesktopDeviceSettings";
import { getDeviceModel } from "../Inventory/Settings/ProvisionedDeviceSettingsRouter";
import AddPolicyModal from "./AddPolicyModal";
import EnableAccountProvisioningModal from "./EnableAccountProvisioningModal";
import "./PolicyList.scss";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { ExportDevicePolicy } from "./ExportDevicePolicy";
import { PolicyDeviceList } from "./PolicyDeviceList";

export type PolicyListQueryParams = "PolicyFilterState";

type PolicyModelBucket = {
  name: string;
  uuid: Maybe<string>;
  policyIds: Maybe<string>[];
  settings: number;
  count: number;
  policyType?: PolicyType;
};

export const DeletePolicyModal: React.FC<{
  selected: Maybe<string>[];
  policies: Array<
    Omit<PolicyModelBucket, "policyIds"> & { policyId: Maybe<string> }
  >;
  setSelected: Dispatch<SetStateAction<Array<string>>>;
}> = ({ selected, policies, setSelected }) => {
  const { t } = useTranslation();
  const returnTo = useModalDispatch();
  const [addNotification] = useNotification();
  const { data, fetching } = useHardwareModelsQuery();
  const [, deletePolicies] = useDeletePoliciesMutation();
  const [, deleteGroupPolicies] = useDeleteGroupPoliciesMutation();

  let desktopModelIds: string[] = [];
  if (data) {
    desktopModelIds = data.hardwareModels
      .filter(model => model.supportsPolicies)
      .map(model => model.id);
  }

  const policiesToRemove = uniq(
    selected.reduce((names, id) => {
      const policyObject = policies.find(policy => policy.policyId === id);
      return policyObject ? [...names, policyObject.name] : names;
    }, [] as Array<string>)
  );

  const devicePolicies: string[] = [];
  const desktopDevicePolices: string[] = [];

  selected.forEach(id => {
    const policyObject = policies.find(policy => policy.policyId === id);
    if (id) {
      policyObject?.uuid && desktopModelIds.includes(policyObject.uuid) //if there's no uuid in the policy, we'll assume its not a desktop policy
        ? desktopDevicePolices.push(id)
        : devicePolicies.push(id);
    }
  });

  const removePolicies = async () => {
    let success = true;
    if (devicePolicies.length) {
      const { error } = await deletePolicies({ policyIds: devicePolicies });
      if (error) {
        success = false;
      }
    }

    if (desktopDevicePolices.length) {
      const { error } = await deleteGroupPolicies({
        policyIds: desktopDevicePolices
      });
      if (error) {
        success = false;
      }
    }

    if (success) {
      returnTo();
      setSelected([]);
      setTimeout(() => {
        addNotification({
          type: "success",
          message: t("POLICIES_DELETED"),
          dismiss: DISMISS.DEFAULT
        });
      }, 100);
    }
  };

  return (
    <DefaultModalForm
      title={t("POLICY_DELETE", { count: policiesToRemove.length })}
      onSubmit={removePolicies}
      name=""
    >
      {({ isSubmitting }) => (
        <DetailsLoader loading={fetching} title={t("LOADING")}>
          <span>
            {t("POLICY_DELETE_CONFIRM", { count: policiesToRemove.length })}
          </span>
          <div className="small-text overflow-list modal-body pt-1 pl-1">
            {policiesToRemove.map((entry, i) => (
              <p key={i}>{entry}</p>
            ))}
          </div>
          <div className="btn-container">
            <CancelButton onClick={returnTo} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="remove"
              labelKey="CONFIRM"
            />
          </div>
        </DetailsLoader>
      )}
    </DefaultModalForm>
  );
};

const PolicyListRouter: React.FC = () => {
  return (
    <Switch>
      <Route
        nest
        path="/:scope"
        render={props => {
          const params = props.match.params as Scope;
          return params.scope === "device" ? (
            <PolicyDeviceList {...props} />
          ) : (
            <PolicyList {...props} />
          );
        }}
      />
      <Redirect nest to="/model" />
    </Switch>
  );
};

export type Scopes = "site" | "group" | "model" | "user_group" | "device";

const PolicyRow: React.FC<{
  model: PolicyModelBucket;
  shownColumns: Array<string>;
  onCheck: (id: string) => void;
  checked: boolean;
  scope: Scopes;
  entityId: string;
  "data-testid": string;
}> = ({ model, shownColumns, onCheck, scope, checked, entityId, ...rest }) => {
  const { COLUMNS } = TABLES.POLICIES;
  const { name, count, settings } = model;

  let linkSearch = `deviceModel=${name}&previous=model`;
  if (scope === PolicyTypes.SITE) {
    linkSearch = `deviceModel=${name}&site=${entityId}&previous=site`;
  }
  if (scope === PolicyTypes.GROUP) {
    linkSearch = `deviceModel=${name}&group=${entityId}&previous=group`;
  }
  if (scope === PolicyTypes.USER_GROUP) {
    linkSearch = `deviceModel=${name}&user_group=${entityId}&previous=user_group`;
  }

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link
            persist={["PolicyFilterState"]}
            to={{
              pathname: "/manage/policy/details/settings",
              search: linkSearch
            }}
          >
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.DEVICES:
        content = count;
        break;
      case COLUMNS.SETTINGS:
        content = settings;
        break;
    }
    return (
      <td key={column} data-testid={`${column}-cell-for-${name}`}>
        {content}
      </td>
    );
  };

  return (
    <TableBulkItemRow
      data-testid={rest["data-testid"]}
      checked={checked}
      onCheck={() => onCheck(name)}
      replaceCheckWith={
        <input
          type="checkbox"
          data-testid={`check-item-${name}`}
          onChange={() => onCheck(name)}
          checked={checked}
          disabled={model.count > 0}
        />
      }
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export type Scope = {
  scope: Scopes;
};

export const PolicyList: React.FC<RouteComponentProps> = ({ match }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const params = match.params as Scope;
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [filterState, setFilter] = usePolicyFilterValue();
  const [sort, setSort] = useState({
    field: "name",
    direction: "ASC"
  } as SortState);
  const [searchTerm, setSearch] = useState<undefined | string>(undefined);
  const { scope } = params;

  const {
    data: provData,
    fetching: fetchingAccountProv
  } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenantId },
    context: noOpNotifyContext
  });
  const account = provData?.getProvisioningAccounts?.[0];
  const provDisabled = !account || !!account.isDisabled;

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.POLICIES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_MODEL"),
    [COLUMNS.DEVICES]: t("DEVICE_COUNT"),
    [COLUMNS.SETTINGS]: t("SETTINGS_COUNT")
  };

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const listScopeRule =
    scope === PolicyTypes.GROUP && filterState.group
      ? [{ equal: { key: scope, value: filterState.group } }]
      : scope === PolicyTypes.SITE && filterState.site
      ? [{ equal: { key: scope, value: filterState.site } }]
      : scope === PolicyTypes.USER_GROUP && filterState.user_group
      ? [{ equal: { key: scope, value: filterState.user_group } }]
      : scope === PolicyTypes.MODEL
      ? [{ equal: { key: "type", value: "model" } }]
      : [];

  // Get all policies at this scope
  const { data: policies, fetching: policiesFetching } = usePoliciesQuery({
    variables: {
      tenantId: tenantId || "",
      scope: scopeToPolicyScope(scope),
      siteId: scope === PolicyTypes.SITE ? filterState.site : undefined,
      groupId: scope === PolicyTypes.GROUP ? filterState.group : undefined
    },
    pause:
      provDisabled ||
      scope === PolicyTypes.USER_GROUP ||
      listScopeRule.length === 0
  });

  // Get all desktop device policies for this scope
  const {
    data: groupPolicies,
    fetching: groupPoliciesFetching
  } = useGroupPoliciesQuery({
    variables: {
      rules: {
        and: [
          { equal: { key: "tenantId", value: tenantId || "" } },
          ...listScopeRule
        ]
      }
    },
    pause: provDisabled || listScopeRule.length === 0
  });

  // Get all the sites for this tenant
  const { data: sites, fetching: sitesFetching } = useSitesForTenantQuery({
    variables: {
      tenantId: tenantId || ""
    },
    pause: scope !== PolicyTypes.SITE
  });

  // Get all the groups for this tenant
  const { data: groups, fetching: groupsFetching } = useAllPoliciesGroupsQuery({
    variables: {
      tenantId: tenantId || ""
    },
    pause: scope !== PolicyTypes.GROUP
  });

  // Get all user groups for this tenant
  const {
    data: userGroups,
    fetching: userGroupsFetching
  } = useUserGroupsForTenantQuery({
    variables: {
      tenantId: tenantId || ""
    },
    pause: scope !== PolicyTypes.USER_GROUP
  });
  // Create a map of hardwarModel:deviceCount of that model in this tenant/site/group, that hardwareModel should support policies and should be provision onboarded(if non desktop)

  // Get all models that support policies from product catalog

  const { data: allHardwareModels } = useHardwareModelsQuery();
  let policySupportedModels: HardwareModel[] = [];
  if (allHardwareModels) {
    policySupportedModels = (allHardwareModels.hardwareModels as HardwareModel[])
      .filter(model => model.supportsProvisioning || model.supportsPolicies) // allow policies only for models that support provisioning or support policies
      .map(model => {
        const policyFormatModel = getDeviceModel(model.name);
        return {
          ...model,
          name: policyFormatModel
        };
      });
  }

  // Get all provision onboarded devices in this tenant/site(if site UUID exists) aggregation of model and count
  const {
    data: deviceCountsData,
    fetching: deviceCountsFetching
  } = useDeviceCountsQuery({
    variables: {
      filter: {
        AND: [
          {
            field: "tenantId",
            eq: tenantId
          },
          {
            field: "siteUUID",
            eq: scope === PolicyTypes.SITE ? filterState.site : undefined
          },
          {
            OR: [
              {
                // used to filter only provisioned devices
                field: "provisioningEnabled",
                eq: "true"
              },
              {
                // used to filter only desktop devices except for "Lens Desktop", which doesn't support policies yet
                field: "proxyAgent",
                eq: DESKTOP_PROXY_AGENT
              }
            ]
          }
        ]
      },
      aggregations: [
        {
          term: "hardwareModel"
        }
      ]
    },
    // Can't get device counts of each model type with this query for groups
    pause: scope === PolicyTypes.GROUP || scope === PolicyTypes.USER_GROUP
  });

  // Get all device data for devices in this group, pause if scope !== group
  const {
    data: devicesInGroup,
    fetching: groupDevicesCountFetching
  } = useDeviceGroups(scope !== PolicyTypes.GROUP);

  const {
    data: userGroupDeviceData,
    fetching: userGroupDeviceDataFetching
  } = useGetUserGroupDevicesQuery({
    variables: {
      tenantId: tenantId,
      id: filterState.user_group || ""
    },
    pause: scope !== PolicyTypes.USER_GROUP || !filterState.user_group
  });

  let deviceCounts: Array<GenericBucket> = [];
  let models: Record<string, PolicyModelBucket> = {};

  if (
    (scope === PolicyTypes.MODEL || scope === PolicyTypes.SITE) &&
    deviceCountsData
  ) {
    if (
      deviceCountsData?.deviceAggregations?.[0]?.buckets &&
      isArrayOfGenericBucket(deviceCountsData.deviceAggregations[0].buckets)
    ) {
      deviceCounts = deviceCountsData.deviceAggregations[0].buckets;

      if (deviceCounts.length > 0) {
        deviceCounts
          .filter(
            (bucket): bucket is KeyBucket => bucket.__typename === "KeyBucket"
          )
          .forEach(bucket => {
            const deviceModel = policySupportedModels.find(
              model =>
                model.name === getDeviceModel(bucket.key) ||
                model.aliases?.includes(getDeviceModel(bucket.key))
            );
            if (deviceModel) {
              // if the model supports policies
              if (!models[deviceModel.name]) {
                models[deviceModel.name] = {
                  name: deviceModel.name,
                  uuid: deviceModel.id,
                  policyIds: [],
                  settings: 0,
                  count: bucket.count
                };
              } else {
                models[deviceModel.name].count += bucket.count;
              }
            }
          });
      }
    }
  } else if (
    scope === PolicyTypes.GROUP &&
    devicesInGroup &&
    devicesInGroup.length > 0
  ) {
    devicesInGroup
      .find(group => group.id === filterState.group)
      ?.devices?.forEach(device => {
        if (
          device.provisioningEnabled ||
          device.proxyAgent === DESKTOP_PROXY_AGENT
        ) {
          const deviceModel = policySupportedModels.find(
            model => model.name === getDeviceModel(device.hardwareModel || "")
          );
          if (deviceModel) {
            if (!models[deviceModel.name]) {
              models[deviceModel.name] = {
                name: deviceModel.name,
                uuid: deviceModel.id,
                policyIds: [],
                settings: 0,
                count: 1
              };
            } else {
              models[deviceModel.name].count++;
            }
          }
        }
      });
  } else if (scope === PolicyTypes.USER_GROUP) {
    const userGroupDevices = userGroupDeviceData?.getUserGroup.users.flatMap(
      user => user?.user?.connections
    );

    (userGroupDevices || []).forEach(device => {
      if (device && device.supportsSettings && device.model?.supportsPolicies) {
        const deviceModel = policySupportedModels.find(
          model => model?.name === getDeviceModel(device?.model?.name || "")
        );
        if (deviceModel) {
          if (!models[deviceModel.name]) {
            models[deviceModel.name] = {
              name: deviceModel.name,
              uuid: deviceModel.id,
              policyIds: [],
              settings: 0,
              count: 1
            };
          } else {
            models[deviceModel.name].count++;
          }
        }
      }
    });
  }

  let siteOptions: {
    text: string;
    value: string;
  }[] = useMemo(() => [], []);
  if (sites) {
    siteOptions = sites.sites
      .map(site => ({
        text: site.name,
        value: site.id
      }))
      .filter(site => !nameIsUnassigned(site.text))
      .sort((a, b) => {
        return a.text.localeCompare(b.text);
      });
  }
  let groupOptions: {
    text: string;
    value: string;
  }[] = useMemo(() => [], []);
  if (groups && groups.getAllGroups && groups.getAllGroups.groups) {
    groupOptions = groups.getAllGroups.groups.groups
      .map(group => ({
        text: group.name,
        value: group.id
      }))
      .sort((a, b) => {
        return a.text.localeCompare(b.text);
      });
  }

  let userGroupOptions: {
    text: string;
    value: string;
  }[] = useMemo(() => [], []);
  if (userGroups && userGroups.userGroupsForTenant) {
    userGroupOptions = userGroups.userGroupsForTenant
      .map(userGroup => ({
        text: userGroup.name,
        value: userGroup.id
      }))
      .sort((a, b) => {
        return a.text.localeCompare(b.text);
      });
  }

  const loading =
    policiesFetching ||
    sitesFetching ||
    groupsFetching ||
    userGroupsFetching ||
    deviceCountsFetching ||
    groupDevicesCountFetching ||
    userGroupDeviceDataFetching;

  // Create a map with the keys being the deviceModel, and the values being the policy info
  // Since settings policies and SW Update policies are unique entities, there may be more than one policy for a device model
  // But we only want to show one row per model policy, since the policy details page merges those policies anyways

  if (policies && policies.getPolicies) {
    policies.getPolicies
      .filter(
        policy =>
          policy &&
          policy.deviceModel &&
          policy.policyId &&
          policy.policyDeploymentScope === scopeToPolicyScope(scope)
      )
      .forEach(policy => {
        if (policy) {
          // If an entry in the map doesn't exist for the deviceModel, add it in.
          // Or if we added in the DeviceSWUpdate policy, the other fields that contain the settings info need to be added
          const { policyId, deviceModelUuid, deviceModel, policyType } = policy;
          const modelName = getDeviceModel(deviceModel!);
          if (modelName && !models[modelName]) {
            models[modelName] = {
              name: deviceModel!,
              uuid: deviceModelUuid,
              policyIds: [policyId],
              settings: 0,
              count: 0,
              policyType: policyType!
            };
          } else {
            models[modelName]?.policyIds.push(policyId);
          }
          if (policy?.policyType === PolicyType.DeviceSettingsUpdate) {
            models[modelName].settings =
              policy.configurationAttributes?.length ?? 0;
          }
        }
      });
  }

  if (groupPolicies && groupPolicies.policiesCapabilities) {
    groupPolicies.policiesCapabilities
      .filter(policy => policy && policy.id && policy.type === scope)
      .forEach(policy => {
        if (policy) {
          // If an entry in the map doesn't exist for the deviceModel, add it in.
          // Or if we added in the DeviceSWUpdate policy, the other fields that contain the settings info need to be added
          const { id, hardwareModel } = policy;
          const deviceModelUuid = hardwareModel?.id;
          const deviceModel = hardwareModel?.name;
          const modelName = getDeviceModel(deviceModel!);
          if (modelName && !models[modelName]) {
            models[modelName] = {
              name: deviceModel!,
              uuid: deviceModelUuid || "",
              policyIds: [id],
              settings: policy.settingsCount || 0,
              count: 0
            };
          } else {
            models[modelName].policyIds.push(id);
            models[modelName].settings = policy.settingsCount;
          }
        }
      });
  }

  let mergedModels: PolicyModelBucket[] = Object.values(models);

  const toggleItem = toggleItemWithSet(
    mergedModels.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  sortTableData(mergedModels, sort);

  if (searchTerm) {
    mergedModels = mergedModels.filter(model =>
      model.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
    );
  }

  const selectedMinusHeader = selectedItems.filter(id => id !== HEADER_ID);

  const emptyList = noSiteGroupOrUserGroup(
    scope,
    siteOptions,
    groupOptions,
    userGroupOptions
  );

  useEffect(() => {
    setSelectedItems([]);
  }, []);

  useEffect(() => {
    if (
      scope === PolicyTypes.SITE &&
      !sitesFetching &&
      !filterState.site &&
      siteOptions.length
    ) {
      setFilter({
        ...filterState,
        site: siteOptions[0].value
      });
    }
  }, [scope, setFilter, sitesFetching, siteOptions, filterState]);

  useEffect(() => {
    if (
      scope === PolicyTypes.GROUP &&
      !groupsFetching &&
      !filterState.group &&
      groupOptions.length
    ) {
      setFilter({
        ...filterState,
        group: groupOptions[0].value
      });
    }
  }, [scope, setFilter, groupsFetching, groupOptions, filterState]);

  useEffect(() => {
    if (
      scope === PolicyTypes.USER_GROUP &&
      !userGroupsFetching &&
      !filterState.user_group &&
      userGroupOptions.length
    ) {
      setFilter({
        ...filterState,
        user_group: userGroupOptions[0].value
      });
    }
  }, [scope, setFilter, userGroupsFetching, userGroupOptions, filterState]);

  const entityId =
    scope === PolicyTypes.SITE
      ? filterState.site!
      : scope === PolicyTypes.GROUP
      ? filterState.group!
      : scope === PolicyTypes.USER_GROUP
      ? filterState.user_group!
      : "";

  const noSitesGroupsOrUserGroups =
    !loading &&
    noSiteGroupOrUserGroup(scope, siteOptions, groupOptions, userGroupOptions);

  const siteGroupOrUserGroupScope =
    scope === PolicyTypes.SITE ||
    scope === PolicyTypes.GROUP ||
    scope === PolicyTypes.USER_GROUP;

  const getDocsLink = (scope: string) => {
    switch (scope) {
      case PolicyTypes.SITE:
        return DOCS.ASSIGN_DEVICE_TO_SITE;
      case PolicyTypes.GROUP:
        return DOCS.MANAGE_DEVICE_GROUPS;
      case PolicyTypes.USER_GROUP:
        return DOCS.MANAGE_DEVICE_USER_GROUPS;
      default:
        return undefined;
    }
  };

  return (
    <Content className={`policy ${scope}`}>
      <Content.Title>{t(scopeToT(scope))}</Content.Title>
      {noSitesGroupsOrUserGroups ? (
        <div data-testid="no-results-animation">
          <NoResultsIndicatorAnimation
            className="mt-2 inline"
            title={
              scope === PolicyTypes.SITE
                ? t("NO_SITES_FOR_POLICIES_TITLE")
                : scope === PolicyTypes.GROUP
                ? t("NO_GROUPS_FOR_POLICIES_TITLE")
                : t("NO_USER_GROUPS_FOR_POLICIES_TITLE")
            }
            titleStyle={"mb-5 animation-title"}
          >
            <div className="animation-title mb-15">
              {scope === PolicyTypes.SITE
                ? t("NO_SITES_FOR_POLICIES_HELP")
                : scope === PolicyTypes.GROUP
                ? t("NO_GROUPS_FOR_POLICIES_HELP")
                : t("NO_USER_GROUPS_FOR_POLICIES_HELP")}
              <HelpLink location={getDocsLink(scope)} testId="doc-link">
                {t("HERE")}
              </HelpLink>
            </div>
            {scope === PolicyTypes.SITE ? (
              <ProtectedLink
                to="/manage/sites/add"
                requirement="UPDATE_SITES"
                data-testid="add-site"
                className="btn btn-primary"
              >
                {t("CREATE_SITE")}
              </ProtectedLink>
            ) : scope === PolicyTypes.GROUP ? (
              <ProtectedLink
                to="/manage/device-groups/add"
                requirement="UPDATE_SITES"
                data-testid="add-group"
                className="btn btn-primary"
              >
                {t("CREATE_A_DEVICE_GROUP")}
              </ProtectedLink>
            ) : (
              <ProtectedLink
                to="/manage/device-users/groups/group/create"
                requirement="UPDATE_SITES"
                data-testid="add-group"
                className="btn btn-primary"
              >
                {t("CREATE_DEVICE_USER_GROUP")}
              </ProtectedLink>
            )}
          </NoResultsIndicatorAnimation>
        </div>
      ) : (
        <>
          <Trans i18nKey={scopeToTSubHeader(scope)}>
            <p className="policy-list-text">
              Settings applied to these policies will only be inherited by
              devices
              <strong> associated with this group.</strong>
            </p>
          </Trans>
          <p className="policies-disclaimer">{t("POLICIES_DISCLAIMER")}</p>
          {scope === PolicyTypes.SITE && (
            <PolicyFilter
              title="Site"
              field="site"
              filterValues={filterState}
              options={siteOptions}
              onChange={value =>
                setFilter({
                  ...filterState,
                  site: value !== "" ? value : undefined
                })
              }
              search
            />
          )}
          {scope === PolicyTypes.GROUP && (
            <PolicyFilter
              title="Group"
              field="group"
              filterValues={filterState}
              options={groupOptions}
              onChange={value =>
                setFilter({
                  ...filterState,
                  group: value !== "" ? value : undefined
                })
              }
              search
            />
          )}
          {scope === PolicyTypes.USER_GROUP && (
            <PolicyFilter
              title="User Group"
              field="user_group"
              filterValues={filterState}
              options={userGroupOptions}
              onChange={value =>
                setFilter({
                  ...filterState,
                  user_group: value !== "" ? value : undefined
                })
              }
              search
            />
          )}
          <div className="pb-1 table-actions">
            <ProtectedLink
              to={infiniteNestLink("/add")}
              className={`btn btn-primary ${loading ? "disabled" : ""}`}
              requirement="update:device"
              data-testid="add-policy-link"
              disabled={loading}
            >
              {t("ADD")}
            </ProtectedLink>
            <Link
              to={infiniteNestLink("/delete")}
              className={`btn ${selectedItems.length ? "" : "disabled"}`}
              data-testid="delete-policy-link"
            >
              {t("DELETE")}
            </Link>
            {!siteGroupOrUserGroupScope && <ExportDevicePolicy />}
            <TableSearchEdit
              search={{
                searchPlaceholder: t("SEARCH_DEVICE_MODEL"),
                onSearch: setSearch
              }}
              edit={{
                tableName: TABLE_NAME,
                shownColumns: shown,
                hiddenColumns: hidden
              }}
            />
          </div>
          <div className="table-container">
            <table className="table custom-table">
              <TableBulkItemHeader
                checked={itemChecked(HEADER_ID)}
                onCheck={() =>
                  toggleAll(
                    mergedModels
                      .filter(model => model.count === 0)
                      .map(entry => entry.name ?? ""),
                    selectedItems,
                    setSelectedItems
                  )
                }
                replaceCheckWith={
                  <input
                    type="checkbox"
                    data-testid="check-all"
                    onChange={() =>
                      toggleAll(
                        mergedModels
                          .filter(model => model.count === 0)
                          .map(entry => entry.name ?? ""),
                        selectedItems,
                        setSelectedItems
                      )
                    }
                    checked={itemChecked(HEADER_ID)}
                    disabled={
                      mergedModels.filter(model => model.count === 0).length ===
                      0
                    } // If there are no user created policies, disable checkbox
                  />
                }
              >
                {generateTableHeaders(shown, getTableHeaders)}
              </TableBulkItemHeader>
              <TableBody
                loading={loading}
                cols={shown.length}
                accommodateScrollbar
              >
                {!emptyList &&
                  mergedModels.map(entry => (
                    <PolicyRow
                      data-testid={`${entry.name}-row`}
                      key={entry.name}
                      model={entry}
                      scope={scope}
                      entityId={entityId}
                      shownColumns={shown.map(column => column.key)}
                      checked={itemChecked(entry.name ?? "")}
                      onCheck={() => toggleItem(entry.name ?? "")}
                    />
                  ))}
                {!policiesFetching &&
                !groupPoliciesFetching &&
                (!mergedModels.length || emptyList) ? (
                  <tr>
                    <td>
                      <NoResultsIndicator className="inline mt-5" />
                    </td>
                  </tr>
                ) : null}
              </TableBody>
            </table>
          </div>
          {selectedMinusHeader.length > 0 && (
            <Route nest path="/delete">
              <DeletePolicyModal
                selected={selectedMinusHeader.flatMap(
                  model => models[model]?.policyIds
                )}
                policies={mergedModels.flatMap(model =>
                  model.policyIds.map(policyId => ({
                    name: model.name,
                    uuid: model.uuid,
                    count: model.count,
                    settings: model.settings,
                    policyType: model.policyType,
                    policyId
                  }))
                )}
                setSelected={setSelectedItems}
              />
            </Route>
          )}
          {!noSitesGroupsOrUserGroups && (
            <ProtectedRoute nest path="/add" requirement="update:device">
              <AddPolicyModal
                existingPolicies={mergedModels.map(bucket => {
                  return { model: bucket.name, uuid: bucket.uuid };
                })}
              />
            </ProtectedRoute>
          )}
        </>
      )}
      {provDisabled && !fetchingAccountProv && (
        <EnableAccountProvisioningModal tenant={tenantId ?? ""} />
      )}
    </Content>
  );
};

const noSiteGroupOrUserGroup = (
  scope: Scopes,
  siteOptions: Array<FilterOption>,
  groupOptions: Array<FilterOption>,
  userGroupOptions: Array<FilterOption>
) => {
  switch (scope) {
    case "site":
      return !siteOptions.length;
    case "group":
      return !groupOptions.length;
    case "user_group":
      return !userGroupOptions.length;
  }
  return false;
};

export const scopeToPolicyScope = (scope: string) => {
  switch (scope) {
    case "site":
      return PolicyDeploymentScope.Site;
    case "group":
      return PolicyDeploymentScope.Group;
    case "model":
      return PolicyDeploymentScope.DeviceModel;
    default:
      return undefined;
  }
};

type PolicyListKeys =
  | "SITE"
  | "POLICY_GROUP"
  | "DEVICE_MODEL"
  | "DEVICE_USER_GROUP"
  | "INDIVIDUAL_DEVICES"
  | "";

export const scopeToT = (scope: string): PolicyListKeys => {
  switch (scope) {
    case "user_group":
      return "DEVICE_USER_GROUP";
    case "site":
      return "SITE";
    case "group":
      return "POLICY_GROUP";
    case "model":
      return "DEVICE_MODEL";
    case "device":
      return "INDIVIDUAL_DEVICES";
    default:
      return "";
  }
};

type PolicyListHeaderKeys =
  | "POLICY_LIST_SUBHEADER_SITE"
  | "POLICY_LIST_SUBHEADER_POLICY_GROUP"
  | "POLICY_LIST_SUBHEADER_DEVICE_MODEL"
  | "POLICY_LIST_SUBHEADER_DEVICE_USER_GROUP"
  | "POLICY_LIST_SUBHEADER_INDIVIDUAL_DEVICES"
  | "";
export const scopeToTSubHeader = (scope: string): PolicyListHeaderKeys => {
  const key = scopeToT(scope);

  return key ? (`POLICY_LIST_SUBHEADER_${key}` as const) : "";
};

export default PolicyListRouter;
